import { Component, lazy, Suspense, useEffect, useId, useRef, useState } from 'react';
import { supabase } from './supabase';
import { ThemeProvider, useTheme } from './ThemeContext';
import SearchModal from './components/SearchModal';
import Paywall from './components/Paywall';
import Onboarding from './components/Onboarding';
import FeedbackButton from './components/FeedbackButton';
import { capture, captureError, identify, phReset } from './lib/posthog';
import './index.css';

// Lazy-loaded pages — split into separate chunks to reduce initial bundle
const Landing = lazy(() => import('./pages/Landing'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const TermsOfService = lazy(() => import('./pages/legal/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const DataProcessingAgreement = lazy(() => import('./pages/legal/DataProcessingAgreement'));
const AcceptableUsePolicy = lazy(() => import('./pages/legal/AcceptableUsePolicy'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const JobDetail = lazy(() => import('./pages/JobDetail'));
const CompanySettings = lazy(() => import('./pages/CompanySettings'));
const Customers = lazy(() => import('./pages/Customers'));
const Quotes = lazy(() => import('./pages/Quotes'));
const Materials = lazy(() => import('./pages/Materials'));
const Vendors = lazy(() => import('./pages/Vendors'));
const PurchaseOrders = lazy(() => import('./pages/PurchaseOrders'));
const Insights = lazy(() => import('./pages/Insights'));
const Agents = lazy(() => import('./pages/Agents'));
const Billing = lazy(() => import('./pages/Billing'));
const Admin = lazy(() => import('./pages/Admin'));
const LinkedInGTM = lazy(() => import('./pages/LinkedInGTM'));
const EmailGTM = lazy(() => import('./pages/EmailGTM'));
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'));
const FreeInvoice = lazy(() => import('./pages/FreeInvoice'));
const PasswordReset = lazy(() => import('./pages/PasswordReset'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ShopFloor = lazy(() => import('./pages/ShopFloor'));
const CustomerPortal = lazy(() => import('./pages/CustomerPortal'));
const SubcontractorPortal = lazy(() => import('./pages/SubcontractorPortal'));
const QBOCallback = lazy(() => import('./pages/QBOCallback'));
const TimeClock = lazy(() => import('./pages/TimeClock'));
const Schedule  = lazy(() => import('./pages/Schedule'));
const Production = lazy(() => import('./pages/Production'));
const Help      = lazy(() => import('./pages/Help'));

// ── Error boundary — catches render crashes and shows a recovery screen ──────
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] caught error:', error, info?.componentStack);
    captureError(error, { source: 'react', component_stack: info?.componentStack?.slice(0, 4000) });
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          minHeight: '100vh', backgroundColor: '#FAFAF8', fontFamily: 'system-ui, sans-serif',
          gap: 12, padding: 32, textAlign: 'center',
        }}>
          <div style={{ fontSize: 40, lineHeight: 1 }}>⚠️</div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' }}>Something went wrong</h2>
          <p style={{ margin: 0, fontSize: 14, color: '#6B7280', maxWidth: 360 }}>
            An unexpected error occurred. Refreshing the page usually fixes it.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: 8, padding: '10px 28px', backgroundColor: '#D97706', color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Refresh the page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Sidebar nav icons (Lucide-style inline SVG) ───────────────────────────────
function NavIcon({ name, size = 18 }) {
  const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', style: { display: 'block', flexShrink: 0 } };
  switch (name) {
    case 'dashboard':  return <svg {...p}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>;
    case 'quotes':     return <svg {...p}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>;
    case 'materials':  return <svg {...p}><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>;
    case 'vendors':    return <svg {...p}><rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>;
    case 'orders':     return <svg {...p}><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>;
    case 'customers':  return <svg {...p}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>;
    case 'schedule':   return <svg {...p}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><rect x="7" y="14" width="3" height="3" rx="0.5"/><rect x="14" y="14" width="3" height="3" rx="0.5"/></svg>;
    case 'production': return <svg {...p}><path d="M3 21V9l6 3V8l6 4V5h6v16H3z"/><path d="M7 21v-4h3v4M15 16h2M15 12h2"/></svg>;
    case 'insights':   return <svg {...p}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>;
    case 'agents':     return <svg {...p}><rect x="4" y="8" width="16" height="12" rx="2"/><path d="M12 8V4"/><circle cx="12" cy="3" r="1"/><line x1="9" y1="13" x2="9" y2="14"/><line x1="15" y1="13" x2="15" y2="14"/></svg>;
    case 'billing':    return <svg {...p}><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>;
    case 'admin':      return <svg {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
    case 'settings':   return <svg {...p}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>;
    case 'search':     return <svg {...p}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
    case 'logout':     return <svg {...p}><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></svg>;
    case 'help':       return <svg {...p}><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
    case 'bell':       return <svg {...p}><path d="M18 8a6 6 0 00-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>;
    case 'sun':        return <svg {...p}><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="6.34" y2="6.34"/><line x1="17.66" y1="17.66" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="6.34" y2="17.66"/><line x1="17.66" y1="6.34" x2="19.07" y2="4.93"/></svg>;
    case 'moon':       return <svg {...p}><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>;
    case 'clock':      return <svg {...p}><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>;
    case 'alert':      return <svg {...p}><path d="M10.3 2.9L1.8 17a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 2.9a2 2 0 00-3.4 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
    default:           return null;
  }
}

function TrackShopMark({ size = 18 }) {
  const gradientId = useId();
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 256 256"
      role="img"
      aria-label="TrackShop"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <defs>
        <linearGradient id={gradientId} x1="36" y1="28" x2="220" y2="228" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FBBF24" />
          <stop offset="1" stopColor="#D97706" />
        </linearGradient>
      </defs>
      <rect x="16" y="16" width="224" height="224" rx="50" fill={`url(#${gradientId})`} />
      <path fill="#090909" d="M63 65h130l-22 31H85L63 65Zm22 47h86l22 31H63l22-31Zm-22 47h130l-22 31H85l-22-31Z" />
    </svg>
  );
}

function TrackShopLogo({ markSize = 20 }) {
  return (
    <>
      <TrackShopMark size={markSize} />
      <span>TrackShop</span>
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <Suspense fallback={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', backgroundColor: '#FFFBF5' }}>
            <span style={{ color: '#9CA3AF', fontSize: 14, fontFamily: 'system-ui, sans-serif' }}>Loading…</span>
          </div>
        }>
          <AppContent />
        </Suspense>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

function AppContent() {
  const { darkMode, t, toggleDarkMode } = useTheme();
  const [session, setSession] = useState(undefined); // undefined = loading
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [view, setView] = useState('landing'); // 'landing' | 'login' | 'signup'
  const [legalPage, setLegalPage] = useState(null); // 'terms' | 'privacy' | 'dpa' | 'aup'
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showCustomers, setShowCustomers] = useState(false);
  const [showQuotes, setShowQuotes] = useState(false);
  const [showMaterials, setShowMaterials] = useState(false);
  const [showVendors, setShowVendors] = useState(false);
  const [showPurchaseOrders, setShowPurchaseOrders] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [showAgents, setShowAgents] = useState(false);
  const [showBilling, setShowBilling] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showProduction, setShowProduction] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const isTrackShopAdmin = session?.user?.email === 'juliuszhang1@gmail.com';

  // Onboarding
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingCompanyId, setOnboardingCompanyId] = useState(null);
  const [onboardingCompanyName, setOnboardingCompanyName] = useState('');

  // Trial banner
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [paywallActive, setPaywallActive] = useState(false);
  const [trialExpiredAt, setTrialExpiredAt] = useState(null);

  // Global search
  const [showSearch, setShowSearch] = useState(false);

  // Mobile menu
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);

  // True while the onboarding useEffect is running its async DB checks.
  // Keeps the loading spinner up so the dashboard never flashes before the
  // wizard (or not) is ready to show.
  const [checkingOnboarding, setCheckingOnboarding] = useState(false);

  // Inactivity timeout
  const [inactivityToast, setInactivityToast] = useState(null);
  const signoutTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  // Prevents INITIAL_SESSION null from clearing the loading state while a
  // confirmation token is being exchanged asynchronously.
  const tokenProcessingRef = useRef(false);
  // Tracks the token type currently being exchanged so onAuthStateChange
  // can react correctly when verifyOtp fires SIGNED_IN instead of PASSWORD_RECOVERY.
  const tokenTypeRef = useRef(null);
  // Set to true before calling signOut() manually (sign-out button) so the
  // SIGNED_OUT handler knows not to show the "session expired" message.
  const manualSignOutRef = useRef(false);
  // Non-empty when the session expired or the inactivity timeout fired —
  // shown as a banner on the Login page.
  const [sessionExpiredMsg, setSessionExpiredMsg] = useState('');

  // Loading-stuck recovery — refs stay current every render so the
  // visibilitychange event handler never closes over stale values.
  const loadingStartRef = useRef(Date.now()); // reset to null once loading ends
  const isLoadingRef   = useRef(true);        // mirrors session===undefined||checkingOnboarding
  // true after 6 s in loading state — shows the "Refresh" escape link
  const [loadingLong, setLoadingLong] = useState(false);

  // Capture ?ref= referral code early — persists to localStorage so Signup.js can use it
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) localStorage.setItem('ts_ref', ref);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenHash = params.get('token_hash');
    const tokenType = params.get('type');
    // Also handle ?code= (Supabase PKCE flow)
    const code = params.get('code');
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const accessToken = hashParams.get('access_token');

    // If the user landed on /reset-password, Supabase always appends
    // type=recovery — but as a safety net, default to 'recovery' on this
    // path even if the param is missing.
    const isResetPath = window.location.pathname === '/reset-password';
    const effectiveTokenType = tokenType || (isResetPath && tokenHash ? 'recovery' : null);

    // PRE-SET token processing flags BEFORE registering the listener.
    // onAuthStateChange fires INITIAL_SESSION synchronously (or on the first
    // microtask) when there is no stored session. If tokenProcessingRef is still
    // false at that moment, the suppression guard below does not trigger and
    // setSession(null) fires — rendering the landing page before verifyOtp
    // has a chance to run. Pre-setting the flags here closes that race.
    if (tokenHash && effectiveTokenType) {
      console.log('[Auth] token_hash + type detected in URL:', effectiveTokenType, '— pre-setting processing flags before listener');
      tokenProcessingRef.current = true;
      tokenTypeRef.current = effectiveTokenType;
    } else if (code) {
      console.log('[Auth] PKCE code detected in URL — pre-setting processing flag before listener');
      tokenProcessingRef.current = true;
    } else if (accessToken) {
      const hashType = hashParams.get('type');
      console.log('[Auth] access_token in hash, type:', hashType ?? 'none', '— pre-setting processing flag before listener');
      if (hashType) tokenTypeRef.current = hashType;
      tokenProcessingRef.current = true;
    }

    // Register the auth state listener. Flags above ensure that any null
    // INITIAL_SESSION during a token exchange is correctly suppressed.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('[Auth] onAuthStateChange:', _event, session ? `user=${session.user.id}` : 'no session');

      // ── Signed out (manual, inactivity timeout, or natural JWT expiry) ─────
      if (_event === 'SIGNED_OUT') {
        console.log('[Auth] SIGNED_OUT — resetting all state');
        phReset();
        // Reset every page flag so there's no stale state when the user logs back in.
        setShowAdmin(false);
        setShowBilling(false);
        setShowSettings(false);
        setShowCustomers(false);
        setShowQuotes(false);
        setShowMaterials(false);
        setShowVendors(false);
        setShowPurchaseOrders(false);
        setShowInsights(false);
        setShowAgents(false);
        setShowSchedule(false);
        setShowProduction(false);
        setSelectedJobId(null);
        setShowOnboarding(false);
        setShowPasswordReset(false);
        setCheckingOnboarding(false);
        setTrialDaysLeft(null);
        setPaywallActive(false);
        setTrialExpiredAt(null);
        setNotifications([]);
        setInactivityToast(null);
        if (!manualSignOutRef.current) {
          // Session expired or inactivity timeout — land on Login with message
          setSessionExpiredMsg('Your session expired — please sign in again');
          setView('login');
        }
        manualSignOutRef.current = false;
        setSession(null);
        return;
      }

      // While a confirmation token is being exchanged, ignore INITIAL_SESSION
      // null events — they would flash the landing page before the token is
      // processed and a valid session is established.
      if (!session && tokenProcessingRef.current && _event === 'INITIAL_SESSION') {
        console.log('[Auth] suppressing null INITIAL_SESSION during token exchange');
        return;
      }

      // Token exchange complete (or never started) — clearing this flag
      // also signals the access_token fallback timer to no-op if it fires.
      tokenProcessingRef.current = false;

      // PASSWORD_RECOVERY fires when Supabase processes the reset token on their
      // domain and redirects back to us with the session already established in
      // the hash. Return early so we never reach setCheckingOnboarding — that
      // would put the app into a loading state and hide the reset form.
      if (_event === 'PASSWORD_RECOVERY') {
        console.log('[Auth] PASSWORD_RECOVERY — showing password reset, skipping onboarding check');
        tokenTypeRef.current = null;
        if (session?.user) identify(session.user.id, { email: session.user.email });
        setShowPasswordReset(true);
        setSession(session);
        return;
      }

      // verifyOtp() with type='recovery' fires SIGNED_IN (not PASSWORD_RECOVERY)
      // in some Supabase versions. INITIAL_SESSION can also carry a recovery
      // session when Supabase processes the hash before our listener registers.
      // Both cases: return early to skip setCheckingOnboarding.
      if ((_event === 'SIGNED_IN' || _event === 'INITIAL_SESSION') && tokenTypeRef.current === 'recovery') {
        console.log('[Auth]', _event, 'from recovery token — showing password reset, skipping onboarding check');
        tokenTypeRef.current = null;
        if (session?.user) identify(session.user.id, { email: session.user.email });
        setShowPasswordReset(true);
        setSession(session);
        return; // do NOT call setCheckingOnboarding for password reset flows
      }

      // type='invite' means a team member invite link — treat as a new user
      // (the onboarding useEffect will handle provisioning their company row).
      // No special handling needed beyond the normal SIGNED_IN flow.
      tokenTypeRef.current = null;

      if (session?.user) {
        identify(session.user.id, { email: session.user.email });
        // Only trigger the onboarding check on the initial session resolution
        // or a fresh sign-in. TOKEN_REFRESHED fires every time the tab becomes
        // visible (Supabase refreshes the JWT in the background) — calling
        // setCheckingOnboarding(true) there puts the app into a loading state
        // even though the user is already authenticated. The onboarding
        // useEffect is keyed on session?.user?.id which hasn't changed, so it
        // never re-runs to clear the flag, causing a persistent loading screen.
        if (_event === 'INITIAL_SESSION' || _event === 'SIGNED_IN') {
          setCheckingOnboarding(true);
        }
      }
      setSession(session);
    });

    // Now trigger the initial session resolution / token exchange.
    // Note: tokenProcessingRef and tokenTypeRef are already set above (pre-listener).
    if (tokenHash && effectiveTokenType) {
      // Always clean up to '/' — removes /reset-password from the address bar
      // and strips the token params so they can't be reused on refresh.
      window.history.replaceState({}, '', '/');
      console.log('[Auth] calling verifyOtp — token_hash present, type:', effectiveTokenType);
      supabase.auth.verifyOtp({ token_hash: tokenHash, type: effectiveTokenType })
        .then(({ data, error }) => {
          if (error || !data?.session) {
            // Token may have already been consumed by detectSessionInUrl or a
            // duplicate call. Fall back to getSession() in case the session was
            // already established — don't blindly set null.
            console.log('[Auth] verifyOtp failed:', error?.message, '— falling back to getSession');
            tokenProcessingRef.current = false;
            const pendingType = tokenTypeRef.current;
            tokenTypeRef.current = null;
            supabase.auth.getSession().then(({ data: { session: s } }) => {
              console.log('[Auth] fallback getSession:', s ? `found session user=${s.user.id}` : 'no session');
              if (s?.user) {
                if (pendingType === 'recovery') {
                  console.log('[Auth] fallback: recovery session confirmed — showing password reset');
                  setShowPasswordReset(true);
                  setSession(s);
                } else {
                  setCheckingOnboarding(true);
                  setSession(s);
                }
              } else {
                setSession(null);
              }
            });
          } else {
            // verifyOtp succeeded. For recovery: set state directly here rather
            // than relying solely on onAuthStateChange timing — this guarantees
            // the reset form shows even if PASSWORD_RECOVERY fires before or
            // after our listener is fully set up.
            console.log('[Auth] verifyOtp succeeded — session:', data.session.user.id, 'type:', effectiveTokenType);
            tokenProcessingRef.current = false;
            if (effectiveTokenType === 'recovery') {
              console.log('[Auth] verifyOtp recovery confirmed — directly showing password reset form');
              tokenTypeRef.current = null; // prevent double-handling in onAuthStateChange SIGNED_IN
              setShowPasswordReset(true);
              setSession(data.session);
              // onAuthStateChange will also fire PASSWORD_RECOVERY; setShowPasswordReset
              // is idempotent and setSession with the same session is a no-op.
            }
            // For non-recovery types (invite, magiclink, etc.), let onAuthStateChange
            // handle setSession + setCheckingOnboarding via SIGNED_IN.
          }
        });
    } else if (code) {
      // PKCE confirmation code — Supabase JS defaults to implicit flow, so
      // detectSessionInUrl does NOT exchange ?code= automatically. We must
      // call exchangeCodeForSession() ourselves. Save the full URL before
      // stripping the code from the address bar.
      tokenProcessingRef.current = true;
      const codeUrl = window.location.href;
      window.history.replaceState({}, '', window.location.pathname);
      console.log('[Auth] PKCE code detected — calling exchangeCodeForSession');
      supabase.auth.exchangeCodeForSession(codeUrl)
        .then(({ data, error }) => {
          if (error || !data?.session) {
            // Exchange failed (e.g. code expired or already used). Fall back
            // to getSession() in case the session was already stored.
            console.log('[Auth] exchangeCodeForSession failed:', error?.message, '— falling back to getSession');
            tokenProcessingRef.current = false;
            supabase.auth.getSession().then(({ data: { session: s } }) => {
              console.log('[Auth] fallback getSession:', s ? `user=${s.user.id}` : 'no session');
              if (s?.user) { identify(s.user.id, { email: s.user.email }); setCheckingOnboarding(true); }
              setSession(s ?? null);
            });
          }
          // On success, exchangeCodeForSession fires SIGNED_IN via
          // onAuthStateChange, which calls setSession + setCheckingOnboarding.
        });
    } else if (accessToken) {
      // Implicit flow hash — detectSessionInUrl handles automatically.
      // We clean the hash immediately so the token isn't re-processed on
      // future renders, then wait for PASSWORD_RECOVERY or SIGNED_IN.
      // Read 'type' from the hash (e.g. 'recovery') and store it so the
      // onAuthStateChange handler can route correctly if PASSWORD_RECOVERY
      // doesn't fire and only SIGNED_IN arrives.
      const hashType = hashParams.get('type');
      if (hashType) tokenTypeRef.current = hashType;
      tokenProcessingRef.current = true;
      window.history.replaceState({}, '', window.location.pathname);
      console.log('[Auth] access_token in hash, type:', hashType ?? 'none', '— waiting for onAuthStateChange, fallback in 3s');

      const fallbackTimer = setTimeout(async () => {
        if (!tokenProcessingRef.current) return; // event already fired
        console.log('[Auth] fallback: event did not fire, calling getSession()');
        const pendingType = tokenTypeRef.current;
        tokenProcessingRef.current = false;
        tokenTypeRef.current = null;
        const { data: { session: s } } = await supabase.auth.getSession();
        console.log('[Auth] fallback getSession:', s ? `user=${s.user.id}` : 'no session');
        if (s?.user) {
          identify(s.user.id, { email: s.user.email });
          if (pendingType === 'recovery') setShowPasswordReset(true);
          else setCheckingOnboarding(true);
        }
        setSession(s ?? null);
      }, 3000);

      // Store the timer so we can cancel it if SIGNED_IN fires first.
      // We piggyback on the existing subscription cleanup via a ref.
      const origUnsub = subscription.unsubscribe.bind(subscription);
      subscription.unsubscribe = () => { clearTimeout(fallbackTimer); origUnsub(); };
    } else {
      // Normal load — resolve the existing persisted session immediately.
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.user) setCheckingOnboarding(true);
        setSession(session);
      });
    }

    return () => subscription.unsubscribe();
  }, []);

  // Check if onboarding is needed when session resolves.
  // Also handles first-time setup for newly confirmed users — the
  // create_company_and_user RPC runs here (not during signup) so that
  // auth.uid() is valid when the company/user records are inserted.
  useEffect(() => {
    if (!session) {
      setCheckingOnboarding(false);
      return;
    }

    (async () => {
      console.log('[Onboarding] useEffect triggered, user:', session.user.id, session.user.email);
      console.log('[Onboarding] full user object:', JSON.stringify(session.user));
      try {

      const { data: userData, error: userQueryError } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', session.user.id)
        .maybeSingle();

      console.log('[Onboarding] users table query result:', { userData, userQueryError });

      // ── New user: userData row doesn't exist yet ──────────────────────────
      if (!userData) {
        const meta = session.user.user_metadata ?? {};
        // Supabase stores metadata passed during signUp under these keys.
        // Try both snake_case (what we pass) and camelCase as a fallback.
        const companyName = meta.company_name || meta.companyName || '';
        const fullName = meta.full_name || meta.fullName || meta.name || '';

        console.log('[Onboarding] new user — user_metadata:', JSON.stringify(meta));
        console.log('[Onboarding] resolved companyName:', companyName, '| fullName:', fullName);

        if (!companyName) {
          // Metadata is missing (shouldn't happen, but can if the token was
          // exchanged on a different device or the session came via a cached
          // INITIAL_SESSION before verifyOtp ran).
          // Fallback: show onboarding without companyId so the user isn't stuck.
          console.warn('[Onboarding] no company_name in metadata — showing onboarding fallback');
          setOnboardingCompanyId(null);
          setOnboardingCompanyName('');
          setShowOnboarding(true);
          return;
        }

        // 1. Create company + user records
        console.log('[Onboarding] calling create_company_and_user RPC...');
        let { error: rpcError } = await supabase.rpc('create_company_and_user', {
          company_name: companyName,
          full_name: fullName,
          user_email: session.user.email,
        });

        // If the RPC reports a slug/unique conflict (the new RPC handles this
        // internally, but older deployments may not), retry once with a 4-char
        // suffix appended to the company name to guarantee a fresh slug.
        if (rpcError) {
          const isDuplicate =
            rpcError.message?.includes('duplicate key') ||
            rpcError.message?.includes('unique constraint') ||
            rpcError.code === '23505';
          if (isDuplicate) {
            const suffix = Math.random().toString(36).slice(2, 6).toUpperCase();
            const retryName = `${companyName} ${suffix}`;
            console.warn('[Onboarding] slug collision — retrying RPC with name:', retryName);
            ({ error: rpcError } = await supabase.rpc('create_company_and_user', {
              company_name: retryName,
              full_name: fullName,
              user_email: session.user.email,
            }));
          }
        }

        if (rpcError) {
          console.error('[Onboarding] create_company_and_user failed:', rpcError.message, rpcError);
          // Still show onboarding so the user isn't stuck on a blank screen.
          setOnboardingCompanyId(null);
          setOnboardingCompanyName(companyName);
          setShowOnboarding(true);
          return;
        }
        console.log('[Onboarding] RPC succeeded');

        // 2. Get the newly created company_id
        const { data: newUserData } = await supabase
          .from('users')
          .select('company_id')
          .eq('id', session.user.id)
          .single();
        const companyId = newUserData?.company_id;
        if (!companyId) {
          console.error('[Onboarding] could not fetch company_id after RPC');
          setShowOnboarding(true);
          return;
        }

        // 3. Generate + insert referral code for this company
        const prefix = companyName.replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 4).padEnd(4, 'X');
        const digits = String(Math.floor(1000 + Math.random() * 9000));
        await supabase.from('referral_codes').insert({
          company_id: companyId,
          code: `${prefix}${digits}`,
        }).catch(() => {});

        // 4. Track incoming referral (if signup came via a referral link)
        const storedRef = localStorage.getItem('ts_ref');
        if (storedRef) {
          await supabase.rpc('track_referral', {
            ref_code: storedRef,
            new_company_id: companyId,
          }).catch(() => {});
          localStorage.removeItem('ts_ref');
        }

        // 5. Send Day 0 welcome email (fire-and-forget)
        fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/onboarding-emails`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ company_id: companyId, day: 0 }),
          },
        ).catch(() => {});

        // 6. Show onboarding wizard
        console.log('[Onboarding] showing onboarding wizard for companyId:', companyId);
        setOnboardingCompanyId(companyId);
        setOnboardingCompanyName(companyName);
        setShowOnboarding(true);
        return;
      }

      // ── Existing user ─────────────────────────────────────────────────────
      // Fallback: row exists but company_id is somehow null — force onboarding.
      if (!userData?.company_id) {
        console.warn('[Onboarding] existing user row has no company_id — forcing onboarding');
        setOnboardingCompanyId(null);
        setOnboardingCompanyName('');
        setShowOnboarding(true);
        return;
      }
      const cid = userData.company_id;

      const companyRes = await supabase
        .from('companies')
        .select('name, onboarding_completed, created_at')
        .eq('id', cid)
        .single();

      setOnboardingCompanyId(cid);
      setOnboardingCompanyName(companyRes.data?.name ?? '');

      // Show onboarding if the wizard was never completed AND the company was
      // created within the last 24 hours. The 24-hour window prevents the
      // wizard from re-appearing for existing companies that predate this
      // column (they will have onboarding_completed = false by default but
      // were clearly set up long before this feature existed).
      const hoursSinceCreation = (Date.now() - new Date(companyRes.data?.created_at ?? 0).getTime()) / 3_600_000;
      if (!companyRes.data?.onboarding_completed && hoursSinceCreation < 24) {
        setShowOnboarding(true);
      }

      // Fetch trial info + subscription status
      const { data: billingData } = await supabase
        .from('companies')
        .select('current_plan, trial_started_at, subscription_status')
        .eq('id', cid)
        .single();
      if (billingData) {
        const { current_plan, trial_started_at, subscription_status } = billingData;
        const isActive = subscription_status === 'active';

        if (current_plan === 'trial' && trial_started_at) {
          const start = new Date(trial_started_at).getTime();
          const elapsed = Math.floor((Date.now() - start) / (1000 * 60 * 60 * 24));
          const remaining = Math.max(0, 14 - elapsed);
          setTrialDaysLeft(remaining);

          if (remaining === 0 && !isActive) {
            const expiredAt = new Date(start + 14 * 24 * 60 * 60 * 1000);
            setTrialExpiredAt(expiredAt.toISOString());
            setPaywallActive(true);
          }
        } else if (subscription_status === 'cancelled') {
          setPaywallActive(true);
        }
      }

      // Fetch notifications
      const { data: notifData } = await supabase
        .from('notifications')
        .select('*')
        .eq('company_id', cid)
        .order('created_at', { ascending: false })
        .limit(10);
      if (notifData) setNotifications(notifData);
      } finally {
        // Always clear the loading gate so the app never hangs on a spinner.
        setCheckingOnboarding(false);
      }
    })();
  }, [session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchNotifications = async (cid) => {
    const { data } = await supabase
      .from('notifications')
      .select('*')
      .eq('company_id', cid)
      .order('created_at', { ascending: false })
      .limit(10);
    if (data) setNotifications(data);
  };

  const handleMarkAllRead = async () => {
    if (!onboardingCompanyId) return;
    await supabase
      .from('notifications')
      .update({ read: true })
      .eq('company_id', onboardingCompanyId)
      .eq('read', false);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const handleSignOut = async () => {
    manualSignOutRef.current = true; // tells SIGNED_OUT handler: user chose this
    phReset();
    await supabase.auth.signOut();
  };

  // Reset all page states then optionally activate one page.
  // Every nav button must call this so no stale page flag blocks rendering.
  const navTo = (setterFn) => {
    setShowAdmin(false);
    setShowBilling(false);
    setShowSettings(false);
    setShowCustomers(false);
    setShowQuotes(false);
    setShowMaterials(false);
    setShowVendors(false);
    setShowPurchaseOrders(false);
    setShowInsights(false);
    setShowAgents(false);
    setShowSchedule(false);
    setShowProduction(false);
    setShowHelp(false);
    setSelectedJobId(null);
    if (setterFn) setterFn();
  };

  // Inactivity timeout — sign out after 60 minutes, warn at 55 minutes
  useEffect(() => {
    if (!session) return;

    const TIMEOUT_MS  = 60 * 60 * 1000; // 60 minutes
    const WARNING_MS  = 55 * 60 * 1000; // 55 minutes (warn 5 min before)

    const clearTimers = () => {
      clearTimeout(signoutTimerRef.current);
      clearTimeout(warningTimerRef.current);
    };

    const startTimers = () => {
      clearTimers();
      setInactivityToast(null);

      warningTimerRef.current = setTimeout(() => {
        setInactivityToast('You will be signed out in 5 minutes due to inactivity.');
      }, WARNING_MS);

      signoutTimerRef.current = setTimeout(async () => {
        setInactivityToast(null);
        await supabase.auth.signOut();
      }, TIMEOUT_MS);
    };

    const resetTimers = () => startTimers();

    startTimers();
    window.addEventListener('mousemove', resetTimers);
    window.addEventListener('keydown', resetTimers);
    window.addEventListener('click', resetTimers);

    return () => {
      clearTimers();
      window.removeEventListener('mousemove', resetTimers);
      window.removeEventListener('keydown', resetTimers);
      window.removeEventListener('click', resetTimers);
    };
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  // Global search — Cmd+K / Ctrl+K
  useEffect(() => {
    if (!session) return;
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch((o) => !o);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [session]);

  // Page-view tracking — fires whenever the active view changes
  useEffect(() => {
    if (!session) return;
    const page = selectedJobId ? 'Job Detail'
      : showAdmin    ? 'Admin'
      : showBilling  ? 'Billing'
      : showSettings ? 'Settings'
      : showCustomers ? 'Customers'
      : showQuotes   ? 'Quotes'
      : showMaterials ? 'Materials'
      : showVendors  ? 'Vendors'
      : showPurchaseOrders ? 'Purchase Orders'
      : showAgents ? 'Agents'
      : showInsights ? 'Insights'
      : showSchedule ? 'Schedule'
      : showProduction ? 'Production'
      : showHelp ? 'Help'
      : 'Dashboard';
    capture('$pageview', { page });
  }, [!!session, !!selectedJobId, showAdmin, showBilling, showSettings, showCustomers, showQuotes, showMaterials, showVendors, showPurchaseOrders, showInsights, showAgents, showSchedule, showProduction, showHelp]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Loading-stuck recovery ────────────────────────────────────────────────────

  // Tab visibility: when the tab becomes visible and loading is stuck, force-clear
  // the loading state immediately rather than reloading the page.
  // Registered once (empty deps) — reads refs so the closure never goes stale.
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState !== 'visible') return;
      // Public routes don't show the loading screen — skip them
      const path = window.location.pathname;
      if (path.includes('/shopfloor') || path.includes('/portal') || path.includes('/subcontractor') || path.includes('/qbo') || path.includes('/clock')) return;
      if (!isLoadingRef.current) return;
      console.warn('[Loading] Tab restored while stuck — force-clearing loading state');
      setCheckingOnboarding(false);
      // Re-resolve session in case it is still undefined
      supabase.auth.getSession().then(({ data: { session: s } }) => {
        setSession((prev) => prev !== undefined ? prev : (s ?? null));
      });
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Safety net: if session is resolved (non-null) but checkingOnboarding is still
  // true after 3 s, the onboarding effect must have stalled — force-clear it.
  useEffect(() => {
    if (!session || !checkingOnboarding) return;
    const t = setTimeout(() => {
      console.warn('[Loading] Session present but checkingOnboarding stuck >3 s — force-clearing');
      setCheckingOnboarding(false);
    }, 3000);
    return () => clearTimeout(t);
  }, [session, checkingOnboarding]); // eslint-disable-line react-hooks/exhaustive-deps

  // 6-second slow indicator + 8-second absolute force-unstick
  useEffect(() => {
    const isLoading = session === undefined || checkingOnboarding;
    if (!isLoading) { setLoadingLong(false); return; }
    // Show "Refresh" escape link after 6 s
    const slowT = setTimeout(() => setLoadingLong(true), 6000);
    // Absolute maximum: force-clear after 8 s so the app never stays blank
    const forceT = setTimeout(() => {
      console.warn('[Loading] Stuck >8 s — forcing unstick');
      setCheckingOnboarding(false);
      if (session === undefined) {
        // Re-attempt session resolution in case the initial getSession silently failed
        supabase.auth.getSession().then(({ data: { session: s } }) => setSession(s ?? null));
      }
    }, 8000);
    return () => { clearTimeout(slowT); clearTimeout(forceT); };
  }, [session, checkingOnboarding]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute theme-aware styles
  const styles = getStyles(t);

  // Keep loading refs current every render so the visibilitychange handler
  // always reads up-to-date values without needing to re-register.
  // (Safe to assign to refs in the render body — does not cause re-renders.)
  const _isNowLoading = session === undefined || checkingOnboarding;
  isLoadingRef.current = _isNowLoading;
  if (!_isNowLoading) loadingStartRef.current = null;

  // Shop floor public route — render without auth
  const _sfParams = new URLSearchParams(window.location.search);
  const _sfFragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const _kioskToken = _sfFragment.get('token') || _sfParams.get('token');

  // Local visual QA for the admin-only LinkedIn workspace. Production builds
  // compile this branch out, so it never bypasses the admin session check.
  if (process.env.NODE_ENV === 'development' && _sfParams.get('preview') === 'linkedin-gtm') {
    return <LinkedInGTM />;
  }
  if (process.env.NODE_ENV === 'development' && _sfParams.get('preview') === 'email-gtm') {
    return <EmailGTM />;
  }
  if (window.location.pathname === '/unsubscribe') {
    return <Unsubscribe />;
  }
  if (window.location.pathname.includes('/shopfloor') && _sfParams.get('id')) {
    return <ShopFloor companyId={_sfParams.get('id')} kioskToken={_kioskToken} />;
  }

  // Time clock public route — render without auth
  if (window.location.pathname.includes('/clock') && _sfParams.get('id')) {
    return <TimeClock companyId={_sfParams.get('id')} kioskToken={_kioskToken} />;
  }

  // QBO OAuth callback route — render without auth check (user must still be logged in)
  if (window.location.pathname.includes('/qbo/callback')) {
    return <QBOCallback />;
  }

  // Customer portal public route — render without auth
  if (window.location.pathname.includes('/portal') && _sfParams.get('token')) {
    return <CustomerPortal />;
  }

  // Subcontractor portal public route — render without auth
  if (window.location.pathname.includes('/subcontractor') && _sfParams.get('token')) {
    return <SubcontractorPortal />;
  }

  // /reset-password — standalone page, completely bypasses the auth state
  // machine. Supabase has already processed the token on their domain and
  // established a session before redirecting here. The page checks for a
  // session itself and shows the form directly — no loading state, no
  // onboarding check, no complex event routing needed.
  if (window.location.pathname === '/reset-password') {
    return <ResetPassword />;
  }

  // Still resolving session OR running the onboarding DB check — keep the
  // loading spinner so we never flash the landing page or dashboard before
  // we know which view to show.
  if (session === undefined || checkingOnboarding) {
    return (
      <div style={styles.loading}>
        <span style={styles.loadingText}>Loading…</span>
        {loadingLong && (
          <div style={{ marginTop: 18, textAlign: 'center' }}>
            <span style={{ fontSize: 13, color: t.text4 }}>Taking longer than expected — </span>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: 'none', border: 'none', fontSize: 13,
                color: '#D97706', cursor: 'pointer', fontWeight: 600,
                padding: 0, textDecoration: 'underline',
                fontFamily: "'Inter', system-ui, sans-serif",
              }}
            >
              Refresh
            </button>
          </div>
        )}
      </div>
    );
  }

  // Password reset callback — user clicked reset link in email
  if (showPasswordReset && session) {
    return (
      <PasswordReset onSuccess={() => setShowPasswordReset(false)} />
    );
  }

  // Not authenticated
  if (!session) {
    if (legalPage === 'terms') return <TermsOfService onBack={() => setLegalPage(null)} />;
    if (legalPage === 'privacy') return <PrivacyPolicy onBack={() => setLegalPage(null)} />;
    if (legalPage === 'dpa') return <DataProcessingAgreement onBack={() => setLegalPage(null)} />;
    if (legalPage === 'aup') return <AcceptableUsePolicy onBack={() => setLegalPage(null)} />;
    if (view === 'free-invoice') {
      return <FreeInvoice onBack={() => setView('landing')} onGetStarted={() => setView('signup')} />;
    }
    if (view === 'signup') {
      return <Signup onSwitchToLogin={() => setView('login')} onBack={() => { setView('landing'); setSessionExpiredMsg(''); }} />;
    }
    if (view === 'login') {
      return (
        <>
          {/* Session-expired banner — shown when inactivity timeout or JWT expiry triggered sign-out */}
          {sessionExpiredMsg && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
              backgroundColor: '#FEF3C7', borderBottom: '1px solid #FDE68A',
              padding: '11px 20px', display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: 10,
              fontFamily: 'system-ui, sans-serif',
            }}>
              <span style={{ fontSize: 13, color: '#92400E', fontWeight: 500 }}>
                {sessionExpiredMsg}
              </span>
              <button
                onClick={() => setSessionExpiredMsg('')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400E', fontSize: 16, lineHeight: 1, padding: 0 }}
                aria-label="Dismiss"
              >✕</button>
            </div>
          )}
          <Login
            onSwitchToSignup={() => { setView('signup'); setSessionExpiredMsg(''); }}
            onBack={() => { setView('landing'); setSessionExpiredMsg(''); }}
          />
        </>
      );
    }
    return (
      <Landing
        onSignIn={() => setView('login')}
        onGetStarted={() => setView('signup')}
        onLegal={(page) => setLegalPage(page)}
        onFreeInvoice={() => setView('free-invoice')}
      />
    );
  }

  // Authenticated — sidebar layout
  const activePage = selectedJobId ? null
    : showAdmin    ? 'admin'
    : showBilling  ? 'billing'
    : showSettings ? 'settings'
    : showCustomers ? 'customers'
    : showQuotes   ? 'quotes'
    : showMaterials ? 'materials'
    : showVendors  ? 'vendors'
    : showPurchaseOrders ? 'orders'
    : showAgents ? 'agents'
    : showInsights ? 'insights'
    : showSchedule ? 'schedule'
    : showProduction ? 'production'
    : showHelp ? 'help'
    : 'dashboard';

  const mainNavItems = [
    { key: 'dashboard', label: 'Dashboard', icon: 'dashboard', action: () => navTo() },
    { key: 'production', label: 'Production', icon: 'production', action: () => navTo(() => setShowProduction(true)) },
    { key: 'schedule',  label: 'Schedule',  icon: 'schedule',  action: () => navTo(() => setShowSchedule(true)) },
    { key: 'quotes',    label: 'Quotes',    icon: 'quotes',    action: () => navTo(() => setShowQuotes(true)) },
    { key: 'materials', label: 'Materials', icon: 'materials', action: () => navTo(() => setShowMaterials(true)) },
    { key: 'vendors',   label: 'Vendors',   icon: 'vendors',   action: () => navTo(() => setShowVendors(true)) },
    { key: 'orders',    label: 'Purchase Orders', icon: 'orders',    action: () => navTo(() => setShowPurchaseOrders(true)) },
    { key: 'customers', label: 'Customers', icon: 'customers', action: () => navTo(() => setShowCustomers(true)) },
    { key: 'agents',    label: 'Agents',    icon: 'agents',    action: () => navTo(() => setShowAgents(true)) },
    { key: 'insights',  label: 'Insights',  icon: 'insights',  action: () => navTo(() => setShowInsights(true)) },
    { key: 'billing',   label: 'Billing',   icon: 'billing',   action: () => navTo(() => setShowBilling(true)) },
  ];

  const bottomNavItems = [
    ...(isTrackShopAdmin ? [{ key: 'admin', label: 'Admin', icon: 'admin', action: () => navTo(() => setShowAdmin(true)) }] : []),
    { key: 'help',     label: 'Help',     icon: 'help',     action: () => navTo(() => setShowHelp(true)) },
    { key: 'settings', label: 'Settings', icon: 'settings', action: () => navTo(() => setShowSettings(true)) },
  ];

  const renderNavItem = (item) => {
    const isActive = activePage === item.key;
    return (
      <button
        key={item.key}
        title={item.label}
        onClick={item.action}
        className={`sidebar-item${isActive ? ' sidebar-item-active' : ''}`}
      >
        <NavIcon name={item.icon} size={18} />
        <span className="sidebar-label">{item.label}</span>
      </button>
    );
  };

  return (
    <div style={styles.app}>
      {showOnboarding && (
        <Onboarding
          session={session}
          companyId={onboardingCompanyId}
          companyName={onboardingCompanyName}
          onComplete={async () => {
            try {
              // Mark onboarding as completed so login never re-triggers the wizard.
              if (onboardingCompanyId) {
                await supabase.from('companies')
                  .update({ onboarding_completed: true })
                  .eq('id', onboardingCompanyId);
              }
              const { data: { session: refreshed } } = await supabase.auth.refreshSession();
              if (refreshed) setSession(refreshed);
            } catch { }
            setShowOnboarding(false);
          }}
        />
      )}

      {/* ── Left sidebar ─────────────────────────────────────────────────── */}
      <aside className="sidebar">
        <div style={styles.sidebarLogo}>
          <TrackShopLogo markSize={22} />
        </div>
        <nav style={styles.sidebarNav}>
          {mainNavItems.map(renderNavItem)}
        </nav>
        <div style={styles.sidebarBottom}>
          {bottomNavItems.map(renderNavItem)}
        </div>
      </aside>

      {/* ── Right: top bar + page content ────────────────────────────────── */}
      <div style={styles.mainWrapper}>
        {/* Top bar */}
        <header style={styles.topBar}>
          {/* Hamburger — visible on mobile only via CSS */}
          <button
            className="topbar-hamburger"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>

          {/* Search */}
          <button onClick={() => setShowSearch(true)} style={styles.searchHintBtn} title="Search (⌘K)">
            <NavIcon name="search" size={14} />
            <span>Search</span>
            <kbd style={styles.searchHintKbd}>⌘K</kbd>
          </button>

          {/* Right cluster */}
          <div style={styles.topBarRight}>
            {/* Notifications */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => { setShowNotifDropdown((o) => !o); if (onboardingCompanyId) fetchNotifications(onboardingCompanyId); }}
                style={styles.bellBtn}
                aria-label="Notifications"
              >
                <NavIcon name="bell" size={17} />
                {notifications.filter((n) => !n.read).length > 0 && (
                  <span style={styles.notifBadge}>{notifications.filter((n) => !n.read).length}</span>
                )}
              </button>
              {showNotifDropdown && (
                <div style={{ ...styles.notifDropdown, right: 0 }}>
                  <div style={styles.notifDropdownHeader}>
                    <span style={styles.notifDropdownTitle}>Notifications</span>
                    <button onClick={handleMarkAllRead} style={styles.markReadBtn}>Mark all read</button>
                  </div>
                  {notifications.length === 0 ? (
                    <p style={styles.notifEmpty}>No notifications yet.</p>
                  ) : (
                    <div>
                      {notifications.map((n) => (
                        <div key={n.id} style={{ ...styles.notifItem, backgroundColor: n.read ? t.card : t.amberBg }}>
                          <p style={styles.notifMsg}>{n.message}</p>
                          <span style={styles.notifTime}>{timeAgo(n.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Dark mode */}
            <button onClick={toggleDarkMode} style={styles.themeToggleBtn} title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
              <NavIcon name={darkMode ? 'sun' : 'moon'} size={16} />
            </button>

            {/* Email */}
            <span className="topbar-email" style={styles.headerEmail}>{session.user.email}</span>

            {/* Sign out */}
            <button className="topbar-signout" onClick={handleSignOut} style={styles.signOutBtn}>Sign Out</button>
          </div>
        </header>

        {/* Inactivity warning toast */}
        {inactivityToast && (
          <div style={styles.inactivityToast}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><NavIcon name="clock" size={15} />{inactivityToast}</span>
            <button onClick={() => setInactivityToast(null)} style={styles.inactivityToastClose}>✕</button>
          </div>
        )}

        {/* Trial expiry banner */}
        {!paywallActive && trialDaysLeft !== null && trialDaysLeft <= 5 && (
          <div style={styles.trialBanner}>
            <span style={styles.trialBannerText}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><NavIcon name="alert" size={15} />Your free trial expires in <strong>{trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''}</strong>. Upgrade to keep access.</span>
            </span>
            <button onClick={() => navTo(() => setShowBilling(true))} style={styles.trialBannerBtn}>
              Upgrade now
            </button>
          </div>
        )}

        {/* Global search modal */}
        {showSearch && session && (
          <SearchModal
            companyId={onboardingCompanyId}
            onClose={() => setShowSearch(false)}
            onSelectJob={(id) => navTo(() => setSelectedJobId(id))}
            onGoToCustomers={() => navTo(() => setShowCustomers(true))}
            onGoToQuotes={() => navTo(() => setShowQuotes(true))}
            onGoToMaterials={() => navTo(() => setShowMaterials(true))}
          />
        )}

        <main>
          {paywallActive && !isTrackShopAdmin && !showBilling ? (
            <Paywall
              onGoToBilling={() => navTo(() => setShowBilling(true))}
              trialExpiredAt={trialExpiredAt}
            />
          ) : showAdmin ? (
            <Admin session={session} onBack={() => navTo()} />
          ) : showBilling ? (
            <Billing session={session} onBack={() => navTo()} />
          ) : showSettings ? (
            <CompanySettings session={session} onBack={() => navTo()} />
          ) : showCustomers ? (
            <Customers
              session={session}
              onBack={() => navTo()}
              onSelectJob={(id) => navTo(() => setSelectedJobId(id))}
            />
          ) : showQuotes ? (
            <Quotes session={session} onBack={() => navTo()} />
          ) : showMaterials ? (
            <Materials session={session} onBack={() => navTo()} />
          ) : showVendors ? (
            <Vendors session={session} onBack={() => navTo()} />
          ) : showPurchaseOrders ? (
            <PurchaseOrders session={session} onBack={() => navTo()} />
          ) : showAgents ? (
            <Agents
              session={session}
              onBack={() => navTo()}
              onUpgrade={() => navTo(() => setShowBilling(true))}
            />
          ) : showInsights ? (
            <Insights session={session} onBack={() => navTo()} />
          ) : showProduction ? (
            <Production session={session} />
          ) : showSchedule ? (
            <Schedule session={session} onSelectJob={(id) => navTo(() => setSelectedJobId(id))} />
          ) : showHelp ? (
            <Help onBack={() => navTo()} />
          ) : selectedJobId ? (
            <JobDetail
              jobId={selectedJobId}
              session={session}
              onBack={() => setSelectedJobId(null)}
            />
          ) : (
            <Dashboard
              session={session}
              onSelectJob={(id) => setSelectedJobId(id)}
            />
          )}
        </main>
      </div>

      {/* ── Feedback button (floating, bottom-right) ─────────────────────── */}
      <FeedbackButton session={session} companyId={onboardingCompanyId} />

      {/* ── Mobile full-screen overlay ────────────────────────────────────── */}
      {mobileMenuOpen && (
        <div style={styles.mobileOverlay}>
          <div style={styles.mobileOverlayHeader}>
            <span style={styles.headerLogo}>
              <TrackShopLogo markSize={22} />
            </span>
            <button onClick={() => setMobileMenuOpen(false)} style={styles.mobileOverlayClose} aria-label="Close menu">✕</button>
          </div>
          <div style={styles.mobileOverlayNav}>
            <span style={styles.mobileOverlayEmail}>{session.user.email}</span>
            {[...mainNavItems, ...bottomNavItems].map(({ key, label, icon, action }) => (
              <button
                key={key}
                onClick={() => { action(); setMobileMenuOpen(false); }}
                style={styles.mobileOverlayBtn}
              >
                <NavIcon name={icon} size={20} />
                <span>{label}</span>
              </button>
            ))}
          </div>
          <div style={styles.mobileOverlayFooter}>
            <button onClick={() => { handleSignOut(); setMobileMenuOpen(false); }} style={styles.mobileOverlaySignOut}>
              <NavIcon name="logout" size={18} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function timeAgo(ts) {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const getStyles = (t) => ({
  loading: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: t.bg,
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  loadingText: {
    color: t.text4,
    fontSize: 15,
  },
  app: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: t.bg,
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  // Sidebar styles (colors only — layout handled by CSS classes)
  sidebarLogo: {
    height: 56,
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    padding: '0 20px',
    fontSize: 18,
    fontWeight: 800,
    color: '#D97706',
    letterSpacing: 0,
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    flexShrink: 0,
  },
  sidebarNav: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px 0',
  },
  sidebarBottom: {
    padding: '8px 0 16px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
  },
  // Main content wrapper (flex column: topBar + main)
  mainWrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    backgroundColor: t.bg,
  },
  // Top bar (replaces header)
  topBar: {
    height: 56,
    backgroundColor: t.navBg,
    borderBottom: `1px solid ${t.navBorder}`,
    padding: '0 20px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexShrink: 0,
  },
  topBarRight: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  // headerLogo still used in mobile overlay header
  headerLogo: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 9,
    fontSize: 20,
    fontWeight: 800,
    color: '#D97706',
    letterSpacing: 0,
  },
  headerEmail: {
    fontSize: 13,
    color: t.text3,
    maxWidth: 160,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flexShrink: 1,
  },
  themeToggleBtn: {
    background: 'none',
    border: `1px solid ${t.borderInput}`,
    borderRadius: 6,
    fontSize: 15,
    cursor: 'pointer',
    padding: '4px 8px',
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  settingsBtn: {
    padding: '6px 14px',
    backgroundColor: 'transparent',
    border: `1px solid ${t.borderInput}`,
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    color: t.text2,
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, color 0.15s ease',
  },
  signOutBtn: {
    padding: '6px 14px',
    backgroundColor: 'transparent',
    border: `1px solid ${t.borderInput}`,
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    color: t.text2,
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, color 0.15s ease',
  },
  searchHintBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 12px',
    backgroundColor: t.card2,
    border: `1px solid ${t.border1}`,
    borderRadius: 8,
    fontSize: 13,
    color: t.text3,
    cursor: 'pointer',
    fontFamily: "'Inter', system-ui, sans-serif",
    transition: 'border-color 0.15s ease',
  },
  searchHintKbd: {
    fontSize: 11,
    color: t.text4,
    backgroundColor: t.hover2,
    border: `1px solid ${t.border1}`,
    borderRadius: 4,
    padding: '1px 5px',
    fontFamily: 'inherit',
  },
  bellBtn: {
    position: 'relative',
    background: 'none',
    border: 'none',
    fontSize: 18,
    cursor: 'pointer',
    padding: '2px 4px',
    lineHeight: 1,
  },
  mobileOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: t.card,
    zIndex: 999,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  mobileOverlayHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 20px',
    height: 56,
    borderBottom: `1px solid ${t.border1}`,
    flexShrink: 0,
  },
  mobileOverlayClose: {
    background: 'none',
    border: 'none',
    fontSize: 22,
    color: t.text4,
    cursor: 'pointer',
    padding: 8,
    lineHeight: 1,
  },
  mobileOverlayNav: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  mobileOverlayEmail: {
    display: 'block',
    padding: '16px 24px 8px',
    fontSize: 12,
    color: t.text4,
    fontWeight: 500,
  },
  mobileOverlayBtn: {
    background: 'none',
    border: 'none',
    borderBottom: `1px solid ${t.border2}`,
    textAlign: 'left',
    padding: '0 24px',
    height: 56,
    fontSize: 16,
    fontWeight: 600,
    color: t.text1,
    cursor: 'pointer',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    fontFamily: 'inherit',
  },
  mobileOverlayFooter: {
    padding: '16px 24px 32px',
    borderTop: `1px solid ${t.border1}`,
    flexShrink: 0,
  },
  mobileOverlaySignOut: {
    width: '100%',
    padding: '0 24px',
    height: 52,
    background: 'none',
    border: '1px solid #FECACA',
    borderRadius: 8,
    textAlign: 'left',
    fontSize: 16,
    fontWeight: 600,
    color: '#DC2626',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    fontFamily: 'inherit',
  },
  notifBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: '#DC2626',
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    borderRadius: 999,
    minWidth: 16,
    height: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 3px',
    lineHeight: 1,
  },
  notifDropdown: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    width: 320,
    backgroundColor: t.card,
    border: `1px solid ${t.border1}`,
    borderRadius: 12,
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    zIndex: 1000,
    overflow: 'hidden',
  },
  notifDropdownHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: `1px solid ${t.border2}`,
  },
  notifDropdownTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: t.text1,
  },
  markReadBtn: {
    background: 'none',
    border: 'none',
    fontSize: 12,
    color: '#D97706',
    fontWeight: 600,
    cursor: 'pointer',
    padding: 0,
  },
  notifEmpty: {
    margin: 0,
    padding: '16px',
    fontSize: 13,
    color: t.text4,
    textAlign: 'center',
  },
  notifItem: {
    padding: '12px 16px',
    borderBottom: `1px solid ${t.border2}`,
    cursor: 'default',
  },
  notifMsg: {
    margin: '0 0 4px',
    fontSize: 13,
    color: t.text2,
    lineHeight: 1.4,
  },
  notifTime: {
    fontSize: 11,
    color: t.text4,
  },
  inactivityToast: {
    position: 'fixed',
    bottom: 24,
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#1F2937',
    color: '#F9FAFB',
    padding: '12px 20px',
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 500,
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    maxWidth: 'calc(100vw - 48px)',
  },
  inactivityToastClose: {
    background: 'none',
    border: 'none',
    color: '#9CA3AF',
    cursor: 'pointer',
    fontSize: 14,
    padding: 0,
    lineHeight: 1,
    flexShrink: 0,
  },
  trialBanner: {
    backgroundColor: t.amberBg,
    borderBottom: `1px solid ${t.amberBorder}`,
    padding: '10px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    flexWrap: 'wrap',
  },
  trialBannerText: {
    fontSize: 13,
    color: t.amberText,
    lineHeight: 1.4,
  },
  trialBannerBtn: {
    padding: '5px 14px',
    backgroundColor: '#D97706',
    border: 'none',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 700,
    color: '#fff',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
});
